import { motion } from "framer-motion";
import { AlertTriangle, FlaskConical, Stethoscope, User, Wand2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { MarkdownLite } from "@/components/ui/markdown-lite";
import type { EncounterTurnRead } from "@/features/cases/cases.api";

const ROLE_STYLES: Record<
  EncounterTurnRead["role"],
  { bubble: string; label: string; icon: typeof User }
> = {
  student: {
    bubble:
      "bg-[var(--color-blue)]/10 border-[var(--color-blue)]/25 text-[var(--color-text-primary)]",
    label: "Médico",
    icon: Stethoscope,
  },
  patient: {
    bubble:
      "bg-[var(--color-surface-2)] border-[var(--color-border)] text-[var(--color-text-primary)]",
    label: "Paciente",
    icon: User,
  },
  system: {
    bubble:
      "bg-[var(--color-gold)]/5 border-[var(--color-gold)]/25 text-[var(--color-text-primary)]",
    label: "Sistema",
    icon: Wand2,
  },
};

const KIND_BADGES: Partial<Record<EncounterTurnRead["kind"], string>> = {
  physical_exam: "Examen físico",
  test_request: "Solicitud de examen",
  test_result: "Resultado",
  decision: "Decisión",
  reasoning: "Razonamiento",
  system_note: "Briefing",
};

export function TurnBubble({ turn }: { turn: EncounterTurnRead }) {
  const style = ROLE_STYLES[turn.role];
  const Icon = style.icon;
  const isCritical =
    turn.kind === "physical_exam" &&
    Boolean((turn.meta as { is_critical?: boolean })?.is_critical);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={cn(
        "flex gap-3",
        turn.role === "student" ? "flex-row-reverse" : "flex-row",
      )}
    >
      <div
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border",
          style.bubble,
        )}
      >
        <Icon size={14} />
      </div>
      <div className="flex-1">
        <div
          className={cn(
            "flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--color-text-muted)]",
            turn.role === "student" ? "justify-end" : "justify-start",
          )}
        >
          <span className="font-[var(--font-mono)] font-semibold">
            {style.label}
          </span>
          {KIND_BADGES[turn.kind] && (
            <span className="rounded-full border border-[var(--color-border)] px-2 py-0.5">
              {KIND_BADGES[turn.kind]}
            </span>
          )}
          {isCritical && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-critical)]/15 border border-[var(--color-critical)]/30 px-2 py-0.5 text-[var(--color-critical)]">
              <AlertTriangle size={10} /> crítico
            </span>
          )}
        </div>
        <div
          className={cn(
            "mt-1.5 rounded-xl border px-4 py-3 text-sm leading-relaxed",
            style.bubble,
          )}
        >
          <MarkdownLite content={turn.content} className="prose-clinical" />
          {turn.kind === "test_result" && (
            <div className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--color-text-muted)]">
              <FlaskConical size={12} className="text-[var(--color-gold)]" />
              Examen complementario
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
